import Transformations from 'scoreboard/imports/transform/transformations'
import _ from 'lodash'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO, TEXT } = DataType
import ifOperators from 'scoreboard/imports/transform/if-operators'
/**
 * The transformation step of the import wizard (#13966)
 */
const ViTransformations = Transformations.extend({

	groupBy: 'group',

	initialize: function(options) {
		Transformations.prototype.initialize.apply(this, arguments)
		this.bannerKeyPrefix = 'admin.import.transform.banner'
		if (this.model.get('type') === 'initiative-status') {
			this.bannerKeyPrefix = 'admin.import.transform.initiative.banner'
			this.bannerHighlightedKeys = [
				'initiatives.percentComplete',
				'common.note.note',
				'initiatives.budgetSpentToDate'
			]
		}
	},

	fields: function() {
		// #31642 prevent blowup if preview is not available
		let columns = _.get(this.model.attributes, 'preview.columns') || []
		let fields
		let fieldsGroup = i18n.get('datasets.fields')
		if (this.model.isSpreadsheet()) {
			// note: we handle duplicate column names by putting a number after
			// them. For example Threshold (1), Threshold (2), etc.
			// #31019 the header row is the first non-ignored row
			let headerRowIndex = this.model.headerRowIndex() || 0
			let cells = this.model.get('preview').rows[headerRowIndex].cells
			// counts how many times each cell heading is used. The constructed
			// object will look like { 'Threshold': 3, 'ID': 1 } etc.
			let labelCounts = _.transform(cells, (accumulator, cell) => {
				if (accumulator[cell.value]) {
					accumulator[cell.value]++
				} else {
					accumulator[cell.value] = 1
				}
			}, {})
			// keeps track of how many times each label has been used as we
			// construct unique names for each column. The constructed
			// object will look like { 'Threshold': 3, 'ID': 1 } etc.
			let labelsUsed = {}
			fields = _.map(cells, (cell, index) => {
				let label = cell.value
				// #33709 use raw value of the cell for the field label if it
				// was parsed as yes-no dataType (instead of the boolean value)
				if (cell.dataType === YES_NO) {
					label = cell.rawValue
				}
				// if we need to add a numbered suffix
				if (labelCounts[cell.value] > 1) {
					let number = _.isUndefined(labelsUsed[cell.value]) ? 1 :
						++labelsUsed[cell.value]
					labelsUsed[cell.value] = number
					label += ` (${number})`
				}
				return {
					id: columns[index].label,
					label,
					group: fieldsGroup
				}
			})
		} else {
			fields = _.map(columns, column => ({
				id: column.label,
				label: column.label,
				dataTypes: [ column.dataType ],
				group: fieldsGroup
			}))
		}
		// add "All Records" (only displays in If fields)
		fields.push({
			id: 'all-records',
			label: i18n.get('admin.import.transform.allRecords'),
			group: 'headerless',
			display: 'ifOnly'
		})
		// add "Any KPI or Threshold Value" (only display in If fields for kpi imports)
		fields.push({
			id: 'any-kpi-or-threshold-value',
			label: i18n.get('admin.import.transform.anyKpiOrThresholdValue'),
			group: 'headerless',
			display: 'ifOnly',
			type: 'metric-value'
		})
		// "by not importing record" (only display in Then fields)
		fields.push({
			id: 'do-not-import-record',
			label: i18n.get('admin.import.transform.then.notImportingRecord'),
			group: 'headerless',
			display: 'thenOnly'
		})
		// add "Each of these values" (only display in Then fields for kpi imports)
		fields.push({
			id: 'each-of-these-values',
			label: i18n.get('admin.import.transform.eachOfTheseValues') + '...',
			group: 'headerless',
			display: 'thenOnly',
			type: 'metric-value'
		})
		// make KPI value N/A (only display in Then fields for kpi imports)
		fields.push({
			id: 'make-kpi-value-na',
			label: i18n.get('admin.import.transform.then.makeKpiValueNA'),
			group: 'headerless',
			display: 'thenOnly',
			type: 'metric-value'
		})
		// "Every KPI and threshold value" (only display in then fields for kpi imports)
		fields.push({
			id: 'every-kpi-and-threshold-value',
			label: i18n.get('admin.import.transform.everyKpiAndThresholdValue') + '...',
			group: 'headerless',
			display: 'thenOnly',
			type: 'metric-value'
		})
		// #36099 add regex group transforms to imports
		this.addRegexGroupFields(fields)
		return fields
	},

	/** @override **/
	getOperatorsToDataTypes: function() {
		let operatorsToDataTypes = ifOperators.operatorsToDataTypes
		// #53202 override the entries for 'is-blank' and 'not-blank',
		// because those default to 'date' (see if-operators.js and the enum
		// DatasetsDateTimeFilterOperator.kt)
		// since most value imports fields will not have dataTypes defined
		// to overwrite it, this can lead to some strange formatting
		operatorsToDataTypes['is-blank'] = TEXT
		operatorsToDataTypes['not-blank'] = TEXT
		return operatorsToDataTypes
	},

	/** override**/
	getFieldDatatype(field) {
		// it is unlikely for a field in a value import to have a 'dataType'
		// attribute, but try that first anyway
		let fieldDataType = _.get(field, 'dataType')
		// some value imports (such as database imports), may have a 'dataTypes'
		// attribute, so try that next
		if (_.isNil(fieldDataType)) {
			let dataTypes = _.get(field, 'dataTypes')
			if (!_.isNil(dataTypes)) {
				fieldDataType = dataTypes[0]
			}
		}
		// for value imports with other types of data sources, this will likely
		// be null, and we will end up using the dataType based on the
		// if operator (see getOperatorsToDataTypes())
		return fieldDataType
	},

	/**
	 * @override
	 */
	submit: function() {
		this.saveState()
		if (this.options.wizard) {
			// #32771 if there are any transformations, or if the transformations
			// have changed, update the preview data
			if (!_.isEmpty(this.transformations) ||
				!_.isEqual(this.transformations, this.originalTransformations)) {
				return $.ajax({
					method: 'post',
					data: JSON.stringify(this.model),
					url: 'api/imports/source-data-preview',
					success: response => {
						this.model.set(response)
						this.options.wizard.next()
					},
					error: (xhr) => {
						this.formView.error(xhr)
					}
				})
			} else {
				this.options.wizard.next()
			}
		} else {
			let url = this.url()
			// #28185 clear import status when editing transformations
			url += '/clear'

			return this.model.save({}, {
				url,
				success: () => this.trigger('done', this.model),
				error: (model, response) => this.formView.error(response)
			})
		}
	}

})

export default ViTransformations

ViTransformations.__filename__ = 'imports/vi/vi-transformations.js'
